/* ⚠️ GENERATED from app/services/avatarScene.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarScene.ts — the avatar on screen: one scene, both clients.
//
// 🟥 A PORTRAIT THAT IS A PICTURE ON MONDAY AND A LIVE VIEW ON TUESDAY HAS TO
//    BE THE SAME PORTRAIT. The snapshot every list draws is rendered by THIS
//    class at creation time; the live view in a chat row is THIS class with a
//    GL context. Same landmarks, same framing, same backdrop, same lights — so
//    a row can swap the picture for the live view under your finger and you
//    cannot tell, which is the whole trick behind "3D everywhere" on a phone.
//
// 🟢 THE CIRCLE IS IN THE SCENE, NOT AROUND IT. The view is a little taller
//    than the circle. A mask — a plane with a hole, drawn first, writing depth
//    and no colour — hides everything outside the hole. At rest the hole is
//    the circle. When the avatar "gets out of the circle" the hole grows a
//    slot straight up, exactly as wide as the circle, and the figure rises
//    into it: the head clears the rim, the shoulders stay clipped by the
//    sides. No native masking, no overflow tricks, no second view; a depth
//    buffer is all it needs, and every GL context has one.
//
// 🟢 NOTHING IS KEYFRAMED. MetaPerson and Mixamo rigs are humanoid, so the
//    wave, the nod and the look-around are aimed in WORLD space — "point the
//    upper arm up and outward", "tip the head 6° forward" — and converted into
//    each bone's parent space. That is what makes them right for any avatar
//    without knowing which way a bone's local axes happen to point.
//
// 🟢 RENDER ON DEMAND. `update()` says whether anything changed; a host that
//    only draws when it did keeps a still avatar at zero GPU work. Sixty still
//    avatars cost nothing; that, plus the budget in avatar3d.ts, is the rule
//    that keeps the list scrolling.
//
// 🟢 THIS FILE ONLY KNOWS `three`. The GL context, the canvas, the file cache,
//    how an embedded texture becomes a GPU texture on a phone — a host gives
//    those in. A twin is generated for the web (tools/gen-twins.mjs).

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { portraitFrame, fullBodyFrame, peekHolePolygon, peekSlotHeight, peekRise, PEEK_EXTRA } from './avatar3d';
import { focusCamera, focusChanged } from './avatarFocus';
import { stepBlink } from './avatarBlink';

/* ─── loading ──────────────────────────────────────────────────────────── */

/** Parse a .glb already in memory. The host fetches (a cache, a file, a URL). */
export function parseAvatarGlb(buffer, o = {}) {
  const loader = new GLTFLoader();
  if (o.texturePlugin) loader.register(o.texturePlugin);
  return new Promise((resolve, reject) => {
    let done = false;
    const t = setTimeout(() => {
      if (!done) {
        done = true;
        reject(new Error('avatar parse timeout'));
      }
    }, o.timeoutMs || 30000);
    loader.parse(buffer, '', gltf => {
      if (done) return;
      done = true;
      clearTimeout(t);
      const root = gltf.scene;
      root.traverse(obj => {
        if (obj.isMesh) {
          obj.frustumCulled = false; // skinned meshes move; a stale bound would pop them out
        }
      });
      resolve({
        root,
        landmarks: landmarksOf(root),
        bytes: buffer.byteLength
      });
    }, err => {
      if (!done) {
        done = true;
        clearTimeout(t);
        reject(err);
      }
    });
  });
}

/** One parsed model, many views: a skeleton-aware clone with its own bones. */
export function cloneAvatar(root) {
  return skeletonClone(root);
}

/* ─── landmarks ────────────────────────────────────────────────────────── */

/** 🟢 BONES BY MEANING, NOT BY SPELLING. MetaPerson, Mixamo and Unity-humanoid
 *  rigs name the same joints differently (`Head`, `mixamorig:Head`, `head`,
 *  `Spine1` / `Spine_01`, `RightArm` / `RightUpperArm` / `upperarm_r`). Each
 *  landmark lists its aliases; the lookup is case-insensitive and ignores a
 *  `mixamorig` prefix. A rig that has none of them falls back to the mesh. */
const BONES = {
  headTop: ['HeadTop_End', 'Head_End', 'HeadTop', 'head_end', 'HeadEnd'],
  head: ['Head', 'head'],
  neck: ['Neck', 'neck', 'Neck1'],
  chest: ['Spine1', 'Spine_01', 'spine_01', 'Chest', 'chest', 'Spine2'],
  chestHi: ['Spine2', 'Spine_02', 'UpperChest', 'upper_chest', 'Spine1'],
  hips: ['Hips', 'hips', 'Pelvis', 'pelvis'],
  lEye: ['LeftEye', 'eye_l', 'Eye_L', 'LeftEye_End', 'eyeL'],
  rEye: ['RightEye', 'eye_r', 'Eye_R', 'eyeR'],
  rArm: ['RightArm', 'RightUpperArm', 'upperarm_r', 'UpperArm_R', 'RightShoulder_arm'],
  rForeArm: ['RightForeArm', 'RightLowerArm', 'lowerarm_r', 'LowerArm_R'],
  rHand: ['RightHand', 'hand_r', 'Hand_R'],
  lArm: ['LeftArm', 'LeftUpperArm', 'upperarm_l'],
  lForeArm: ['LeftForeArm', 'LeftLowerArm', 'lowerarm_l']
};
const normName = n => String(n || '').toLowerCase().replace(/^mixamorig[:_]?/, '').replace(/[\s:.]/g, '');

/** The first object whose name matches any alias for `key`. */
export function findBone(root, key) {
  const aliases = (BONES[key] || [String(key)]).map(normName);
  let found = null;
  root.traverse(o => {
    if (found) return;
    const n = normName(o.name);
    if (n && aliases.includes(n)) found = o;
  });
  return found;
}
const _v = new THREE.Vector3();
function worldY(obj) {
  if (!obj) return null;
  obj.getWorldPosition(_v);
  return _v.y;
}

/** Where the body is, from its bones — and from its mesh when a bone is missing. */
export function landmarksOf(root) {
  root.updateWorldMatrix(true, true);
  const box = new THREE.Box3();
  root.traverse(o => {
    if (o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position) {
      /* 🟢 THE REST POSITIONS ONLY. computeBoundingBox() also folds every morph
         target's extent into the box — six targets on the kit's body put the
         "floor" ten centimetres under the feet and the "top" above the head. */
      const b = new THREE.Box3().setFromBufferAttribute(o.geometry.attributes.position).applyMatrix4(o.matrixWorld);
      box.union(b);
    }
  });
  const meshTop = Number.isFinite(box.max.y) ? box.max.y : 1.75;
  const meshFloor = Number.isFinite(box.min.y) ? box.min.y : 0;
  const halfWidth = Number.isFinite(box.max.x) ? Math.max(Math.abs(box.max.x), Math.abs(box.min.x)) : 0.35;
  const headTop = worldY(findBone(root, 'headTop'));
  const head = worldY(findBone(root, 'head'));
  const lEye = worldY(findBone(root, 'lEye'));
  const chest = worldY(findBone(root, 'chest'));
  const neck = worldY(findBone(root, 'neck'));
  const top = headTop != null ? Math.max(headTop, meshTop - 0.02) : meshTop;
  const eyes = lEye != null ? lEye : head != null ? head + 0.06 : top - 0.12;
  // 🟢 "face until the chest": Spine1 is mid-chest on a Mixamo rig. Without it,
  //    a head is about 0.24 of standing height; the chest line sits one head
  //    and a half below the top.
  const headH = head != null ? top - head + 0.05 : (meshTop - meshFloor) * 0.13;
  const chestY = chest != null ? chest : neck != null ? neck - headH * 0.9 : top - headH * 1.9;
  return {
    top,
    chest: chestY,
    eyes,
    floor: meshFloor,
    halfWidth: Math.max(0.15, halfWidth)
  };
}

/* ─── small motion helpers ─────────────────────────────────────────────── */

const easeOutBack = t => {
  const c = 1.70158;
  const x = t - 1;
  return 1 + (c + 1) * x * x * x + c * x * x;
};
const easeInCubic = t => t * t * t;
const clamp01 = t => t < 0 ? 0 : t > 1 ? 1 : t;
const _q1 = new THREE.Quaternion();
const _qp = new THREE.Quaternion();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _p1 = new THREE.Vector3();
const _p2 = new THREE.Vector3();
const _d = new THREE.Vector3();
const _t = new THREE.Vector3();
function rigOf(root, key, childKey) {
  const bone = findBone(root, key);
  if (!bone) return null;
  let restDir = null;
  const child = childKey ? findBone(root, childKey) : null;
  if (child) {
    bone.getWorldPosition(_p1);
    child.getWorldPosition(_p2);
    restDir = _p2.sub(_p1).normalize().clone();
  }
  return {
    bone,
    child,
    rest: bone.quaternion.clone(),
    restDir
  };
}

/** Compose a WORLD-space rotation `qWorld` (scaled by `amount`) onto the bone's
 *  CURRENT orientation. Callers reset a bone to rest at the top of a frame and
 *  stack motions on it in order; a parent moved earlier in the same frame is
 *  read through a fresh world matrix, so a forearm follows the arm it hangs from. */
function applyWorldRotation(r, qWorld, amount) {
  const parent = r.bone.parent;
  if (parent) {
    parent.updateWorldMatrix(true, false);
    parent.getWorldQuaternion(_qp);
  } else _qp.identity();
  // local = parent⁻¹ · world · parent   (scratch quats distinct from the argument)
  _qa.copy(_qp).invert().multiply(qWorld).multiply(_qp);
  _qb.identity().slerp(_qa, clamp01(amount));
  r.bone.quaternion.premultiply(_qb);
}

/** The bone's CURRENT world direction toward its child. */
function currentDir(r, out) {
  if (!r.child) return false;
  r.bone.updateWorldMatrix(true, false);
  r.child.updateWorldMatrix(true, false);
  r.bone.getWorldPosition(_p1);
  r.child.getWorldPosition(_p2);
  out.copy(_p2).sub(_p1);
  if (out.lengthSq() < 1e-10) return false;
  out.normalize();
  return true;
}

/** Aim a bone at a world direction, by `amount` ∈ [0,1], from wherever it is now.
 *  🟥 `target` must not be one of this file's scratch vectors — currentDir()
 *     writes _p1/_p2. Callers use _t. */
function aimBone(r, target, amount) {
  const tx = target.x,
    ty = target.y,
    tz = target.z;
  if (!currentDir(r, _d)) return;
  _q1.setFromUnitVectors(_d, _p1.set(tx, ty, tz).normalize());
  applyWorldRotation(r, _q1, amount);
}
const X = new THREE.Vector3(1, 0, 0);
const Y = new THREE.Vector3(0, 1, 0);
const Z = new THREE.Vector3(0, 0, 1);
const _axisQ = new THREE.Quaternion();
function axisRotation(r, axis, rad, amount = 1) {
  _axisQ.setFromAxisAngle(axis, rad);
  applyWorldRotation(r, _axisQ, amount);
}

/* ─── the scene ────────────────────────────────────────────────────────── */

export const BACKDROP_TOP = '#EAEFFF';
export const BACKDROP_BOTTOM = '#C7D3F4';
export const PORTRAIT_SMILE = 0.35;
export class AvatarScene {
  scene = new THREE.Scene();
  pivot = new THREE.Group(); // rotates about Y
  lift = new THREE.Group(); // rises for the peek
  model = null;
  landmarks = null;
  frame = null;
  mask = null;
  backdrop = null;
  plate = null;
  shadow = null;
  slot = -1; // last slot height the mask was built for
  rig = {};
  morphs = [];

  // state
  rotation = 0; // rad, current
  rotationTarget = 0;
  spin = 0; // rad/s, momentum after a drag
  dragging = false;
  peekP = 0;
  motions = [];
  smileV = 0;
  smileTarget = 0;
  /* 🟢 THE CREATOR LOOKS AT WHAT IS BEING EDITED. A nose on a full-body view is
     four pixels tall, so a face slider was a guess followed by a squint. The
     focus is two fractions (services/avatarFocus.ts) and the camera eases to
     them rather than cutting, because a cut at this zoom reads as a glitch. */
  focus = null;
  camY = 0;
  camZ = 0; // where the camera is now
  camYT = 0;
  camZT = 0; // where it is going
  blinkV = 0;
  breatheOn = false;
  /* 🟢 THE FACE BLINKS ON ITS OWN. The motion has always existed and nothing
     ever asked for it, so a profile sat with its eyes open forever — the one
     thing a face never does. There is no timer: the schedule is a pure function
     (services/avatarBlink.ts) asked on frames a breathing figure is already
     drawing, so it costs two comparisons and wakes nothing up. */
  blinkIdle = false;
  blinkPlan = null;
  lastNow = 0;
  dirty = true;
  constructor(renderer, o) {
    this.renderer = renderer;
    this.mode = o.mode;
    this.width = o.width;
    this.height = o.height;
    this.diameter = o.diameter || o.width;
    renderer.setPixelRatio(o.pixelRatio || 1);
    renderer.setSize(o.width, o.height, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.setClearColor?.(0x000000, 0);
    this.camera = o.mode === 'portrait' ? new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 50) : new THREE.PerspectiveCamera(26, o.width / Math.max(1, o.height), 0.1, 50);
    this.camera.position.set(0, 1, 10);
    this.scene.add(this.camera);

    // 🟢 LIGHTS ONLY, NO ENVIRONMENT MAP — deliberately. A PMREM environment
    //    needs float render targets, which an Expo GL context may not offer,
    //    and a picture rendered on a phone must look like the same avatar
    //    rendered live on the web. Four lights the same everywhere: a soft
    //    sky, a key from front-top-right, a cool fill from the left, a rim
    //    from behind so hair and shoulders separate from the backdrop.
    const hemi = new THREE.HemisphereLight(0xffffff, 0x8f9dc4, 1.6);
    const key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(1.2, 2.2, 3);
    const fill = new THREE.DirectionalLight(0xdfe7ff, 0.8);
    fill.position.set(-2.5, 1, 1.5);
    const rim = new THREE.DirectionalLight(0xffffff, 0.9);
    rim.position.set(0, 2.5, -3);
    this.scene.add(hemi, key, fill, rim);
    this.pivot.add(this.lift);
    this.scene.add(this.pivot);
    if (o.plateColor) {
      const g = new THREE.PlaneGeometry(1000, 1000);
      const m = new THREE.MeshBasicMaterial({
        color: new THREE.Color(o.plateColor),
        depthWrite: false
      });
      this.plate = new THREE.Mesh(g, m);
      this.plate.position.z = -20;
      this.plate.renderOrder = -3;
      this.scene.add(this.plate);
    }
    if (o.mode === 'portrait') {
      if (o.backdrop !== false) this.buildBackdrop(o.backdropTop || BACKDROP_TOP, o.backdropBottom || BACKDROP_BOTTOM);
      this.smileTarget = PORTRAIT_SMILE;
    } else {
      this.buildShadow();
      this.breatheOn = true;
      /* 🟥 FULL BODY ONLY, AND THAT IS DELIBERATE. A portrait is STILL between
         motions — `isAnimating()` says so — so an idle blink there would hold
         the render loop open for every little avatar in a chat list, forever,
         to move four pixels. A full-body figure is already drawing every frame
         to breathe; this rides along on those. */
      this.blinkIdle = true;
      this.blinkPlan = null;
    }
  }
  buildBackdrop(top, bottom) {
    const g = new THREE.CircleGeometry(1, 64);
    const cTop = new THREE.Color(top),
      cBot = new THREE.Color(bottom);
    const pos = g.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const t = (pos.getY(i) + 1) / 2; // -1..1 → 0..1
      c.copy(cBot).lerp(cTop, t);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const m = new THREE.MeshBasicMaterial({
      vertexColors: true,
      depthWrite: false,
      toneMapped: false
    });
    this.backdrop = new THREE.Mesh(g, m);
    this.backdrop.renderOrder = -2;
    this.backdrop.position.z = -3;
    this.scene.add(this.backdrop);
  }
  buildShadow() {
    const g = new THREE.CircleGeometry(1, 48);
    const m = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.16,
      depthWrite: false
    });
    this.shadow = new THREE.Mesh(g, m);
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.renderOrder = -2;
    this.scene.add(this.shadow);
  }

  /* ─── model ─── */

  /** Mount a (cloned) model. Landmarks are measured from it, the camera framed. */
  setModel(model, landmarks) {
    if (this.model) this.lift.remove(this.model);
    this.model = model;
    this.lift.add(model);
    this.landmarks = landmarks || landmarksOf(model);
    this.rig = {
      head: rigOf(model, 'head', 'headTop'),
      neck: rigOf(model, 'neck', 'head'),
      chest: rigOf(model, 'chest', 'chestHi'),
      rArm: rigOf(model, 'rArm', 'rForeArm'),
      rForeArm: rigOf(model, 'rForeArm', 'rHand')
    };
    this.morphs = [];
    model.traverse(o => {
      if (o.isMesh && o.morphTargetDictionary) {
        const d = o.morphTargetDictionary;
        // ARKit names (MetaPerson): mouthSmileLeft/Right; a single mouthSmile where a rig has one
        const smile = d.mouthSmile ?? -1,
          smileL = d.mouthSmileLeft ?? -1,
          smileR = d.mouthSmileRight ?? -1;
        const blinkL = d.eyeBlinkLeft ?? d.EyeBlinkLeft ?? -1,
          blinkR = d.eyeBlinkRight ?? d.EyeBlinkRight ?? -1;
        if (smile >= 0 || smileL >= 0 || smileR >= 0 || blinkL >= 0 || blinkR >= 0) this.morphs.push({
          mesh: o,
          smile,
          smileL,
          smileR,
          blinkL,
          blinkR
        });
      }
    });
    this.frameCamera();
    this.dirty = true;
  }
  resize(width, height, diameter) {
    this.width = width;
    this.height = height;
    if (diameter) this.diameter = diameter;
    this.renderer.setSize(width, height, false);
    this.frameCamera();
    this.dirty = true;
  }
  frameCamera() {
    const l = this.landmarks;
    if (!l) return;
    if (this.mode === 'portrait') {
      const f = portraitFrame(l, this.diameter, this.width, this.height);
      this.frame = f;
      const cam = this.camera;
      cam.left = f.left;
      cam.right = f.right;
      cam.bottom = f.bottom;
      cam.top = f.top;
      cam.position.set(0, 0, 10);
      cam.updateProjectionMatrix();
      if (this.backdrop) {
        this.backdrop.scale.setScalar(f.radius);
        this.backdrop.position.set(0, f.centerY, -3);
      }
      // the figure is placed so the portrait span is centred on the circle
      this.pivot.position.set(0, 0, 0);
      this.slot = -1; // force a mask rebuild
      this.updateMask();
    } else {
      const cam = this.camera;
      cam.aspect = this.width / Math.max(1, this.height);
      const f = this.focus ? focusCamera(l, cam.fov, cam.aspect, this.focus) : fullBodyFrame(l, cam.fov, cam.aspect);
      const lift = this.focus ? 0 : (l.top - l.floor) * 0.02;
      this.camYT = f.targetY + lift;
      this.camZT = f.distance;
      /* 🟥 THE FIRST FRAME DOES NOT EASE. Mounting with the camera at the origin
         and gliding to the figure is a swoop nobody asked for; only a CHANGE of
         focus is animated. */
      if (!this.camZ) {
        this.camY = this.camYT;
        this.camZ = this.camZT;
      }
      cam.position.set(0, this.camY, this.camZ);
      cam.lookAt(0, this.camY - lift, 0);
      cam.updateProjectionMatrix();
      if (this.shadow) {
        this.shadow.position.set(0, l.floor + 0.002, 0);
        this.shadow.scale.set(l.halfWidth * 1.1, l.halfWidth * 0.55, 1);
      }
    }
  }

  /** Where the head is, as a fraction of the view height from the top —
   *  what a speech bubble above the profile needs to sit "close to his head". */
  headAnchor() {
    const l = this.landmarks;
    if (!l) return null;
    if (this.mode === 'fullbody') {
      const cam = this.camera;
      const f = fullBodyFrame(l, cam.fov, cam.aspect);
      return {
        x: 0.5,
        y: clamp01(f.headFraction - 0.06)
      };
    }
    const f = this.frame;
    if (!f) return null;
    return {
      x: 0.5,
      y: clamp01((f.top - l.top) / (f.top - f.bottom))
    };
  }

  /* ─── the mask ─── */

  updateMask() {
    if (this.mode !== 'portrait' || !this.frame) return;
    const f = this.frame;
    const slot = peekSlotHeight(this.peekP, f, this.diameter);
    if (Math.abs(slot - this.slot) < 1e-5 && this.mask) return;
    this.slot = slot;
    const big = Math.max(f.right - f.left, f.top - f.bottom) * 4;
    const outer = new THREE.Shape();
    outer.moveTo(-big, -big);
    outer.lineTo(big, -big);
    outer.lineTo(big, big);
    outer.lineTo(-big, big);
    outer.closePath();
    const poly = peekHolePolygon(f.radius, slot, 64);
    const hole = new THREE.Path();
    poly.forEach(([x, y], i) => {
      if (i === 0) hole.moveTo(x, y);else hole.lineTo(x, y);
    });
    hole.closePath();
    outer.holes.push(hole);
    const g = new THREE.ShapeGeometry(outer);
    if (this.mask) {
      this.mask.geometry.dispose();
      this.mask.geometry = g;
    } else {
      const m = new THREE.MeshBasicMaterial({
        colorWrite: false,
        depthWrite: true,
        depthTest: true
      });
      this.mask = new THREE.Mesh(g, m);
      this.mask.renderOrder = -10; // first: it must own the depth buffer
      this.scene.add(this.mask);
    }
    this.mask.position.set(0, f.centerY, 5);
  }

  /* ─── interaction ─── */

  /** A horizontal drag of `dxPx` pixels rolls the figure; a full width is a full turn. */
  dragBy(dxPx) {
    this.dragging = true;
    const rad = dxPx / Math.max(1, this.width) * Math.PI * 2;
    this.rotation += rad;
    this.rotationTarget = this.rotation;
    this.spin = 0;
    this.dirty = true;
  }
  /** The finger lifted with `vxPxPerS` horizontal velocity; the figure keeps a little spin. */
  dragEnd(vxPxPerS) {
    this.dragging = false;
    this.spin = vxPxPerS / Math.max(1, this.width) * Math.PI * 2 * 0.6;
    this.dirty = true;
  }
  /** Ease back to face the viewer (rows do this a beat after a drag). */
  returnToFront() {
    this.rotationTarget = 0;
    this.spin = 0;
    this.dirty = true;
    this.rotation = wrapPi(this.rotation);
  }
  setRotation(rad) {
    this.rotation = rad;
    this.rotationTarget = rad;
    this.spin = 0;
    this.dirty = true;
  }
  getRotation() {
    return this.rotation;
  }

  /* ─── motions ─── */

  start(kind, dur, now) {
    if (this.motions.some(m => m.kind === kind)) return;
    this.motions.push({
      kind,
      start: now,
      dur
    });
    this.dirty = true;
  }
  /** Rise out of the circle, wave, drop back. ~2.3s. Portrait only. */
  peek(now = nowMs()) {
    if (this.mode === 'portrait') this.start('peek', 2300, now);
  }
  nod(now = nowMs()) {
    this.start('nod', 700, now);
  }
  lookAround(now = nowMs()) {
    this.start('look', 1400, now);
  }
  blink(now = nowMs()) {
    this.start('blink', 200, now);
  }
  wave(now = nowMs()) {
    this.start('wave', 1300, now);
  }
  setSmile(v) {
    this.smileTarget = clamp01(v);
    this.dirty = true;
  }
  /** Whether a host should keep requesting frames. A full-body view breathes, so
   *  it is always animating; a portrait is still between motions. */
  isAnimating() {
    return this.breatheOn || this.motions.length > 0 || this.dragging || Math.abs(this.spin) > 1e-3 || Math.abs(this.rotation - this.rotationTarget) > 1e-3 || Math.abs(this.camZ - this.camZT) > 1e-4 || Math.abs(this.camY - this.camYT) > 1e-4;
  }

  /** Blink on its own, or stop. Full-body figures start with it on. */
  setIdleBlink(on) {
    if (this.blinkIdle === on) return;
    this.blinkIdle = on;
    this.blinkPlan = null;
    this.dirty = true;
  }

  /** Look at the part being edited; null goes back to the whole figure. */
  setFocus(f) {
    if (this.mode !== 'fullbody') return;
    if (!focusChanged(this.focus, f)) return;
    this.focus = f;
    this.frameCamera();
    this.dirty = true;
  }
  peekProgress() {
    return this.peekP;
  }

  /**
   * Advance to `now`. Returns true when the frame changed and should be drawn.
   * Cheap when still: a few comparisons and no matrix work.
   */
  update(now = nowMs()) {
    const dt = this.lastNow ? Math.min(0.05, (now - this.lastNow) / 1000) : 0;
    this.lastNow = now;
    let changed = this.dirty;
    this.dirty = false;

    // rotation: momentum, then ease to target when not dragging
    if (!this.dragging) {
      if (Math.abs(this.spin) > 1e-3) {
        this.rotation += this.spin * dt;
        this.rotationTarget = this.rotation;
        this.spin *= Math.pow(0.02, dt); // ~90% gone in half a second
        if (Math.abs(this.spin) < 1e-3) this.spin = 0;
        changed = true;
      } else if (Math.abs(this.rotation - this.rotationTarget) > 1e-3) {
        this.rotation += (this.rotationTarget - this.rotation) * Math.min(1, dt * 9);
        if (Math.abs(this.rotation - this.rotationTarget) < 1e-3) this.rotation = this.rotationTarget;
        changed = true;
      }
    }
    this.pivot.rotation.y = this.rotation;

    // the camera eases toward the focus
    if (this.mode === 'fullbody' && (Math.abs(this.camY - this.camYT) > 1e-4 || Math.abs(this.camZ - this.camZT) > 1e-4)) {
      const k = Math.min(1, dt * 7);
      this.camY += (this.camYT - this.camY) * k;
      this.camZ += (this.camZT - this.camZ) * k;
      if (Math.abs(this.camY - this.camYT) < 1e-4) this.camY = this.camYT;
      if (Math.abs(this.camZ - this.camZT) < 1e-4) this.camZ = this.camZT;
      const cam = this.camera;
      const l = this.landmarks;
      const lift = this.focus || !l ? 0 : (l.top - l.floor) * 0.02;
      cam.position.set(0, this.camY, this.camZ);
      cam.lookAt(0, this.camY - lift, 0);
      cam.updateProjectionMatrix();
      changed = true;
    }

    // an idle blink, on the frames we are drawing anyway
    if (this.blinkIdle) {
      const st = stepBlink(this.blinkPlan, now, Math.random(), Math.random());
      this.blinkPlan = st.plan;
      if (st.fire) {
        this.blink(now);
        changed = true;
      }
    }

    // smile eases
    if (Math.abs(this.smileV - this.smileTarget) > 1e-3) {
      this.smileV += (this.smileTarget - this.smileV) * Math.min(1, dt * 6);
      changed = true;
    }

    // motions
    let peekP = 0,
      nodA = 0,
      lookA = 0,
      blink = 0,
      waveA = 0,
      waveT = 0;
    const keep = [];
    for (const m of this.motions) {
      const t = clamp01((now - m.start) / m.dur);
      changed = true;
      switch (m.kind) {
        case 'peek':
          {
            // rise 0–0.17, wave 0.13–0.7, hold to 0.82, drop 0.82–1
            if (t < 0.17) peekP = easeOutBack(t / 0.17);else if (t < 0.82) peekP = 1;else peekP = 1 - easeInCubic((t - 0.82) / 0.18);
            if (t > 0.13 && t < 0.7) {
              waveA = Math.sin((t - 0.13) / 0.57 * Math.PI);
              waveT = (t - 0.13) / 0.57;
            }
            break;
          }
        case 'nod':
          nodA = Math.sin(t * Math.PI * 2) * (1 - t * 0.3);
          break;
        case 'look':
          lookA = Math.sin(t * Math.PI * 2) * Math.sin(t * Math.PI);
          break;
        case 'blink':
          blink = Math.sin(t * Math.PI);
          break;
        case 'wave':
          waveA = Math.sin(t * Math.PI);
          waveT = t;
          break;
        default:
          break;
      }
      if (t < 1) keep.push(m);
    }
    this.motions = keep;
    if (this.motions.length === 0 && this.peekP !== 0) {
      this.peekP = 0;
      changed = true;
    } else if (peekP !== this.peekP) {
      this.peekP = peekP;
      changed = true;
    }

    // breathing (full body): slow, tiny, forever
    let breathe = 0;
    if (this.breatheOn) {
      breathe = Math.sin(now / 1000 * 1.1) * 0.5 + 0.5;
      changed = true;
    }
    if (!changed) return false;

    // apply
    if (this.mode === 'portrait' && this.frame) {
      this.lift.position.y = peekRise(this.peekP, this.frame, this.diameter);
      this.updateMask();
    } else {
      this.lift.position.y = 0;
    }
    // bones: back to rest, then the frame's motions stacked in parent-first order
    const R = this.rig;
    for (const k in R) {
      const r = R[k];
      if (r) r.bone.quaternion.copy(r.rest);
    }
    if (R.chest && this.breatheOn) axisRotation(R.chest, X, -0.012 * breathe);
    if (R.head) {
      if (nodA) axisRotation(R.head, X, -0.10 * nodA);
      if (lookA) axisRotation(R.head, Y, 0.35 * lookA);
    }
    if (waveA > 0) {
      // which side the arm is on is read off the rest pose, not assumed
      const side = R.rArm?.restDir ? Math.sign(R.rArm.restDir.x) || -1 : -1;
      if (R.rArm) {
        // upper arm: up and outward, a little forward
        aimBone(R.rArm, _t.set(side * 0.7, 0.8, 0.3), waveA);
      }
      if (R.rForeArm) {
        // forearm: straight up, then swung side to side about the body's forward axis — the "hello"
        aimBone(R.rForeArm, _t.set(side * 0.25, 1, 0.2), waveA);
        const swing = Math.sin(waveT * Math.PI * 4) * 0.5;
        axisRotation(R.rForeArm, Z, swing, waveA);
      }
    }
    // morphs
    const b = Math.max(this.blinkV, blink);
    for (const m of this.morphs) {
      const inf = m.mesh.morphTargetInfluences;
      if (!inf) continue;
      if (m.smile >= 0) inf[m.smile] = this.smileV;
      if (m.smileL >= 0) inf[m.smileL] = this.smileV;
      if (m.smileR >= 0) inf[m.smileR] = this.smileV;
      if (m.blinkL >= 0) inf[m.blinkL] = b;
      if (m.blinkR >= 0) inf[m.blinkR] = b;
    }
    return true;
  }
  render() {
    this.renderer.render(this.scene, this.camera);
  }

  /** The portrait at rest, for the snapshot: no peek, facing front, smiling. */
  poseForSnapshot(now = nowMs()) {
    this.motions = [];
    this.peekP = 0;
    this.rotation = 0;
    this.rotationTarget = 0;
    this.spin = 0;
    this.dragging = false;
    this.smileV = this.smileTarget = PORTRAIT_SMILE;
    this.blinkV = 0;
    this.dirty = true;
    this.update(now);
  }
  dispose() {
    if (this.model) this.lift.remove(this.model);
    this.model = null;
    this.mask?.geometry.dispose();
    this.mask?.material?.dispose?.();
    this.backdrop?.geometry.dispose();
    this.backdrop?.material?.dispose?.();
    this.shadow?.geometry.dispose();
    this.shadow?.material?.dispose?.();
    this.plate?.geometry.dispose();
    this.plate?.material?.dispose?.();
    this.scene.clear();
  }
}
function wrapPi(a) {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r < -Math.PI) r += Math.PI * 2;
  return r;
}
function nowMs() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

/** A minimal `canvas` for a WebGLRenderer given a context it did not create
 *  (Expo GL). three touches width/height/style and listens for context loss. */
export function fakeCanvas(width, height) {
  return {
    width,
    height,
    clientWidth: width,
    clientHeight: height,
    style: {},
    addEventListener() {},
    removeEventListener() {},
    getContext() {
      return null;
    }
  };
}

/** The size the peek view takes for a circle of `d`. Re-exported for hosts. */
export function liveViewSize(d) {
  return {
    width: d,
    height: Math.round(d * (1 + PEEK_EXTRA))
  };
}
